import type { Table } from "@tanstack/react-table";
import { Columns3, Eye, EyeOff, RotateCcw } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { columnName, isStructural } from "./column-name";

/**
 * Ustun ko'rinishi tugmasi — faqat chizadi va almashtiradi. Tanlovni saqlash
 * `useColumnVisibility` da: holatni jadvalga bergan sahifa uni o'zi yozadi.
 */
export function ColumnVisibility<TData>({ table }: { table: Table<TData> }) {
  const { t } = useTranslation();
  const columns = table.getAllLeafColumns().filter(column => !isStructural(column));
  const hidden = columns.filter(column => !column.getIsVisible()).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="relative rounded-xl">
          <Columns3 />
          {t("table.columns")}
          {/* Yashirilgan ustunlar soni — popover yopiq turganda jadval nega
              to'liq emasligini boshqa hech narsa aytmaydi. */}
          <AnimatePresence>
            {hidden > 0 ? (
              <motion.span
                key="count"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="bg-primary text-primary-foreground grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-semibold tabular-nums"
                aria-label={t("table.hiddenCount", { count: hidden })}
              >
                {hidden}
              </motion.span>
            ) : null}
          </AnimatePresence>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 rounded-xl p-1.5">
        <p className="text-muted-foreground px-2 pt-1 pb-2 text-xs font-medium">
          {t("table.columnsHint")}
        </p>
        <div className="flex flex-col gap-0.5">
          {columns.map(column => {
            const name = columnName(column);
            const visible = column.getIsVisible();
            return (
              <label
                key={column.id}
                className="hover:bg-muted flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors"
              >
                <Checkbox
                  aria-label={name}
                  checked={visible}
                  onCheckedChange={checked => column.toggleVisibility(checked === true)}
                />
                <span className="flex-1 truncate">{name}</span>
                {visible ? (
                  <Eye className="text-muted-foreground size-3.5" aria-hidden />
                ) : (
                  <EyeOff className="text-muted-foreground/60 size-3.5" aria-hidden />
                )}
              </label>
            );
          })}
        </div>
        {hidden > 0 ? (
          <button
            type="button"
            onClick={() => table.toggleAllColumnsVisible(true)}
            className="text-muted-foreground hover:bg-muted hover:text-foreground mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg border-t px-2 py-1.5 text-xs"
          >
            <RotateCcw className="size-3.5" />
            {t("table.showAll")}
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
